
import { useState, memo } from "react";
import CreateTaskForm from "./CreateTaskForm";
import SearchBar from "./task-list/SearchBar";
import DeleteTaskDialog from "./task-list/DeleteTaskDialog";
import TaskTabs from "./task-list/TaskTabs";
import OfflineStatus from "./task-list/OfflineStatus";
import { useOfflineSync } from "@/hooks/use-offline-sync";
import { useTasks } from "@/hooks/use-tasks";
import { useAdminRole } from "@/hooks/use-admin-role";
import { useTaskActions } from "./task-list/TaskActions";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

type TaskRow = {
  title: string;
  id: string;
  status: string;
  [key: string]: any;
};

// Memoized SearchBar component
const MemoizedSearchBar = memo(SearchBar);

// Memoized CreateTaskForm component
const MemoizedCreateTaskForm = memo(CreateTaskForm);

// Memoized TaskTabs component
const MemoizedTaskTabs = memo(TaskTabs);

// Memoized DeleteTaskDialog component
const MemoizedDeleteTaskDialog = memo(DeleteTaskDialog);

const TaskList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  
  const { toast } = useToast();
  const isAdmin = useAdminRole();
  const { tasks, loading, fetchTasks } = useTasks(isAdmin);
  const { isSyncing } = useOfflineSync();
  const { handleDelete } = useTaskActions(fetchTasks);

  // Opsæt real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('task-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks'
        },
        async (payload: RealtimePostgresChangesPayload<TaskRow>) => {
          console.log('Task update received:', payload);
          
          const actionText = {
            INSERT: 'oprettet',
            UPDATE: 'opdateret',
            DELETE: 'slettet'
          }[payload.eventType];

          const taskTitle = payload.eventType === 'DELETE' 
            ? payload.old?.title || 'En opgave'
            : payload.new?.title || 'En opgave';

          toast({
            title: `Opgave ${actionText}`,
            description: `${taskTitle} er blevet ${actionText}`,
          });

          await fetchTasks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchTasks, toast]);

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;
    await handleDelete(taskToDelete);
    setTaskToDelete(null);
    setShowDeleteDialog(false);
  };

  const handleEdit = (taskId: string) => {
    setEditingTask(taskId);
    setShowCreateForm(true);
  };

  const handleCreateClick = () => {
    setEditingTask(null);
    setShowCreateForm(true);
  };

  const filteredTasks = tasks.filter((task: any) =>
    task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.assigned_to?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div 
      className="space-y-6"
      role="region" 
      aria-label="Opgaveliste"
    >
      <OfflineStatus isSyncing={isSyncing} />
      
      <MemoizedSearchBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onCreateClick={handleCreateClick}
      />
      
      {showCreateForm && (
        <MemoizedCreateTaskForm 
          onClose={() => {
            setShowCreateForm(false);
            setEditingTask(null);
          }}
          onTaskCreated={fetchTasks}
          editingTaskId={editingTask}
        />
      )}
      
      {loading ? (
        <div 
          className="text-center py-4" 
          role="status" 
          aria-live="polite"
        >
          Indlæser opgaver...
        </div>
      ) : tasks.length === 0 ? (
        <div 
          className="text-center py-4 text-gray-500"
          role="status" 
          aria-live="polite"
        >
          {isAdmin 
            ? "Ingen opgaver fundet. Tilføj nogle opgaver for at komme i gang."
            : "Du har ingen tildelte opgaver på nuværende tidspunkt."}
        </div>
      ) : (
        <MemoizedTaskTabs
          tasks={filteredTasks}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onEdit={handleEdit}
          onDelete={(taskId) => {
            setTaskToDelete(taskId);
            setShowDeleteDialog(true);
          }}
        />
      )}

      <MemoizedDeleteTaskDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default TaskList;
